import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Stack,
  Typography,
  Grid,
  Avatar,
  Chip,
  CircularProgress,
  IconButton,
  Collapse,
  Badge,
  Divider,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Paper,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { alpha } from '@mui/material/styles';

// Icon imports
import BoltIcon from '@mui/icons-material/Bolt';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import HourglassTopRoundedIcon from '@mui/icons-material/HourglassTopRounded';
import PsychologyAltRoundedIcon from '@mui/icons-material/PsychologyAltRounded';
import PublicRoundedIcon from '@mui/icons-material/PublicRounded';
import ShieldRoundedIcon from '@mui/icons-material/ShieldRounded';
import StreamRoundedIcon from '@mui/icons-material/StreamRounded';
import CancelRoundedIcon from '@mui/icons-material/CancelRounded';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded';
import CloseIcon from '@mui/icons-material/Close';
import KeyboardArrowUpRoundedIcon from '@mui/icons-material/KeyboardArrowUpRounded';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';

// Additional icons for Column 3 left avatars
import ArticleRoundedIcon from '@mui/icons-material/ArticleRounded';
import SettingsRoundedIcon from '@mui/icons-material/SettingsRounded';
import PersonRoundedIcon from '@mui/icons-material/PersonRounded';
import LanguageIcon from '@mui/icons-material/Language';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';

const renderSources = (sourcesData) => {
  if (Array.isArray(sourcesData)) {
    if (sourcesData.length === 0) return '-';
    // Split any comma-separated strings within the array and flatten
    const flatSources = sourcesData.flatMap(src => 
      typeof src === 'string' ? src.split(',').map(s => s.trim()).filter(Boolean) : src
    );
    return (
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
        {flatSources.map((src, idx) => {
          let display = src;
          let isLink = false;
          try {
            if (typeof src === 'string' && src.startsWith('http')) {
              const url = new URL(src);
              display = url.hostname.replace(/^www\./, '');
              isLink = true;
            }
          } catch (e) {}
          
          return (
            <Box 
              key={idx}
              component={isLink ? 'a' : 'div'}
              href={isLink ? src : undefined}
              target={isLink ? '_blank' : undefined}
              sx={{ 
                display: 'inline-flex',
                alignItems: 'center',
                gap: 1,
                width: 'fit-content',
                px: 1.25,
                py: 0.5,
                bgcolor: '#f4f8fc', 
                color: '#346db6',
                border: '1px solid #dce8f4',
                borderRadius: '8px',
                textDecoration: 'none',
                transition: 'all 0.2s',
                '&:hover': isLink ? { bgcolor: '#e8f0f8' } : {}
              }} 
            >
              <LanguageIcon sx={{ fontSize: '14px' }} />
              <Typography sx={{ fontSize: '0.7rem', fontWeight: 600 }}>
                {display}
              </Typography>
              {isLink && <OpenInNewIcon sx={{ fontSize: '14px' }} />}
            </Box>
          );
        })}
      </Box>
    );
  }
  return sourcesData || '-';
};

const EvidenceGrid = ({ meta }) => {
  if (!meta) return <Typography variant="body2" sx={{ color: '#64748b' }}>-</Typography>;
  
  const conf = meta.confidence ? String(meta.confidence).toUpperCase() : undefined;
  const confidenceColor = conf === 'HIGH' ? 'success' : conf === 'MEDIUM' ? 'warning' : conf === 'LOW' ? 'error' : 'default';

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Typography variant="body2" sx={{ color: '#64748b', fontSize: '0.8rem' }}>
          Match Count :
        </Typography>
        <Typography variant="body2" sx={{ color: '#0f172a', fontSize: '0.8rem' }}>
          {meta.matchCount ?? '-'}
        </Typography>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Typography variant="body2" sx={{ color: '#64748b', fontSize: '0.8rem' }}>
          Confidence :
        </Typography>
        {meta.confidence ? (
          <Chip size="small" label={conf} color={confidenceColor} sx={{ height: 20, fontWeight: 600, fontSize: '0.65rem', borderRadius: '4px' }} />
        ) : (
          <Typography variant="body2" sx={{ color: '#0f172a', fontSize: '0.8rem' }}>-</Typography>
        )}
      </Box>
      <Box sx={{ mt: 0.5 }}>
        <Typography variant="caption" sx={{ color: '#64748b',textTransform: 'uppercase', letterSpacing: '0.04em', display: 'block', mb: 0.5, fontSize: '0.8rem' }}>
          Sources
        </Typography>
        {renderSources(meta.sources)}
      </Box>
    </Box>
  );
};

const defaultStepsConfig = [
  {
    index: 1,
    stepNumber: 1,
    title: 'Data Steward Agent Workflow',
    status: '-',
    completedAt: '-',
    duration: '-',
    description: '-',
    outcome: '-',
    evidenceHeader: 'EVIDENCE',
    evidence: [
      { label: 'Request Payload', value: '-' },
      { label: 'Schema Validation', value: '-' },
      { label: 'Duplicate Check', value: '-' },
      { label: 'Data Completeness', value: '-' }
    ],
    accent: '#1e88e5',
    state: 'idle'
  },
  {
    index: 2,
    stepNumber: 2,
    title: 'Standardizing Requested Address',
    status: '-',
    completedAt: '-',
    duration: '-',
    description: '-',
    outcome: '-',
    evidenceHeader: 'EVIDENCE',
    evidence: [
      { label: 'Input Address', value: '-' },
      { label: 'Standardized Address', value: '-' },
      { label: 'Match Score', value: '-', isBadge: false }
    ],
    accent: '#16a34a',
    state: 'idle'
  },
  {
    index: 3,
    stepNumber: 3,
    title: 'Retrieving Prescription Data',
    status: '-',
    completedAt: '-',
    duration: '-',
    description: '-',
    outcome: '-',
    evidenceHeader: 'EVIDENCE',
    evidence: [
      { label: 'Query', value: '-' },
      { label: 'Source', value: '-' }
    ],
    accent: '#f59e0b',
    state: 'idle'
  },
  {
    index: 4,
    stepNumber: 4,
    title: 'Analyzing Prescription Volume',
    status: '-',
    completedAt: '-',
    duration: '-',
    description: '-',
    outcome: '-',
    evidenceHeader: 'EVIDENCE',
    evidence: [
      { label: 'Prescription Volume for Current Zip', value: '-' }
    ],
    accent: '#1e88e5',
    state: 'idle'
  },
  {
    index: 5,
    stepNumber: 5,
    title: 'Waiting for Human Review',
    status: '-',
    completedAt: '-',
    enteredStepAt: '-',
    estimatedReviewTime: '-',
    assignedTo: '-',
    slaDueBy: '-',
    reviewPriority: '-',
    description: '-',
    outcome: '-',
    whatHappensNext: '-',
    evidenceHeader: 'EVIDENCE SUMMARY',
    evidence: [
      { label: 'All required data checks completed', leftIconType: 'document', isGreen: false },
      { label: 'Automated validations passed', leftIconType: 'settings', isGreen: false },
      { label: 'Prescription evidence check', leftIconType: 'shield', isGreen: false },
      { label: 'Human decision required', leftIconType: 'person', isGreen: false }
    ],
    notes: '',
    accent: '#ea580c',
    state: 'idle',
    isEvidenceSummaryMode: true
  }
];

const accountChangeStepsConfig = [
  {
    index: 1,
    stepNumber: 1,
    title: 'Agent Workflow Started',
    status: '-',
    completedAt: '-',
    duration: '-',
    description: '-',
    outcome: '-',
    evidenceHeader: 'EVIDENCE',
    evidence: [
      { label: 'Request Payload', value: '-' },
      { label: 'Schema Validation', value: '-' },
      { label: 'Duplicate Check', value: '-' },
      { label: 'Data Completeness', value: '-' }
    ],
    accent: '#1e88e5',
    state: 'idle'
  },
  {
    index: 2,
    stepNumber: 2,
    title: 'Public Source Search',
    status: '-',
    completedAt: '-',
    duration: '-',
    description: '-',
    outcome: '-',
    evidenceHeader: 'EVIDENCE',
    evidence: [
      { label: 'Website', value: 'Healthcare.gov', isClickable: true },
      { label: 'Status', value: 'Completed', isBadge: true, badgeColor: '#16a34a' }
    ],
    accent: '#16a34a',
    state: 'completed'
  },
  {
    index: 3,
    stepNumber: 3,
    title: 'Search Validation',
    status: '-',
    completedAt: '-',
    duration: '-',
    description: '-',
    outcome: '-',
    evidenceHeader: 'EVIDENCE',
    evidence: [],
    accent: '#f59e0b',
    state: 'idle'
  },
  {
    index: 4,
    stepNumber: 4,
    title: 'Review Routing',
    status: '-',
    completedAt: '-',
    duration: '-',
    description: '-',
    outcome: '-',
    evidenceHeader: 'EVIDENCE',
    evidence: [],
    accent: '#8b5cf6',
    state: 'idle'
  },
  {
    index: 5,
    stepNumber: 5,
    title: 'Waiting for Human Review',
    status: '-',
    completedAt: '-',
    enteredStepAt: '-',
    estimatedReviewTime: '-',
    assignedTo: '-',
    slaDueBy: '-',
    reviewPriority: '-',
    description: '-',
    outcome: '-',
    whatHappensNext: '-',
    evidenceHeader: 'EVIDENCE SUMMARY',
    evidence: [
      { label: 'All required data checks completed', leftIconType: 'document', isGreen: false },
      { label: 'Automated validations passed', leftIconType: 'settings', isGreen: false },
      { label: 'Human decision required', leftIconType: 'person', isGreen: false }
    ],
    notes: '',
    accent: '#ea580c',
    state: 'idle',
    isEvidenceSummaryMode: true
  }
];

const formatTimelineKey = (key = '') => {
  const formatted = String(key)
    .replace(/[_-]+/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/^./, (char) => char.toUpperCase());
  
  if (formatted.toLowerCase() === 'prescription volume old trx' || formatted.toLowerCase() === 'prescription volume old trx for old zip') {
    return 'Prescription Volume for Current Zip';
  }
  if (formatted.toLowerCase() === 'prescription volume new trx' || formatted.toLowerCase() === 'prescription volume new trx for new zip') {
    return 'Prescription Volume for Requested Zip';
  }
  if (formatted.toLowerCase() === 'prescription volume old zip') {
    return 'Current Zip';
  }
  if (formatted.toLowerCase() === 'prescription volume new zip') {
    return 'Requested Zip';
  }
  return formatted;
};

const stripStepNumber = (value = '') => String(value).replace(/^\d+\.\s*/, '').trim();

const parseMessagePayload = (message) => {
  if (!message) return null;
  if (typeof message === 'object') return message;

  try {
    return JSON.parse(message);
  } catch {
    return null;
  }
};

const getStepKey = (step = {}) => {
  const explicitKey = String(step.step || step.stepName || step.eventName || step.name || step.title || '').trim();
  if (explicitKey) return explicitKey.toLowerCase();

  const queryKey = String(step.query || step.sqlQuery || step.searchQuery || step.search_query || '').trim();
  if (queryKey) return 'query';

  const payload = parseMessagePayload(step.message || step.subtitle || step.description);
  if (payload && typeof payload === 'object') {
    const payloadKey = String(payload.step || payload.stepName || payload.eventName || payload.name || payload.title || '').trim();
    if (payloadKey) return payloadKey.toLowerCase();

    const payloadQueryKey = String(payload.query || payload.sqlQuery || payload.searchQuery || payload.search_query || '').trim();
    if (payloadQueryKey) return 'query';
  }

  return '';
};

const getTimelineVisuals = (step = {}, index = 0) => {
  const key = `${getStepKey(step)} ${String(step.status || step.state || '').toLowerCase()}`;

  if (key.includes('internet') || key.includes('public') || key.includes('verify') || key.includes('compare')) {
    return { accent: '#38bdf8', iconComponent: PublicRoundedIcon };
  }
  if (key.includes('standardiz') || key.includes('started') || key.includes('steward')) {
    return { accent: '#22c55e', iconComponent: BoltIcon };
  }
  if (key.includes('recommendation') || key.includes('decision') || key.includes('agentic') || key.includes('human review required')) {
    return { accent: '#fb923c', iconComponent: ShieldRoundedIcon };
  }
  if (key.includes('human') || key.includes('manual') || key.includes('review') || key.includes('waiting')) {
    return { accent: '#f97316', iconComponent: HourglassTopRoundedIcon };
  }
  if (key.includes('trx') || key.includes('prescription') || key.includes('query')) {
    return { accent: '#8b5cf6', iconComponent: PsychologyAltRoundedIcon };
  }

  const fallbackVisuals = [
    { accent: '#38bdf8', iconComponent: StreamRoundedIcon },
    { accent: '#22c55e', iconComponent: BoltIcon },
    { accent: '#a78bfa', iconComponent: PsychologyAltRoundedIcon },
    { accent: '#38bdf8', iconComponent: PublicRoundedIcon },
    { accent: '#fb923c', iconComponent: ShieldRoundedIcon },
  ];

  return fallbackVisuals[index] || fallbackVisuals[fallbackVisuals.length - 1];
};

const getStepTemplateIndex = (stepKey, isAccountChange) => {
  if (isAccountChange) {
    if (stepKey.includes('steward_agent') || stepKey.includes('steward agent') || stepKey.includes('started')) return 0;
    if (stepKey.includes('internet_search') || stepKey.includes('internet search')) return 1;
    if (stepKey.includes('search_validation') || stepKey.includes('search validation')) return 2;
    if (stepKey.includes('manual_review') || stepKey.includes('manual review')) return 3;
    if (stepKey.includes('agentic_workflow') || stepKey.includes('agentic workflow') || stepKey.includes('human') || stepKey.includes('waiting')) return 4;
    return -1;
  }

  if (stepKey.includes('steward_agent') || stepKey.includes('steward agent') || stepKey.includes('started review') || stepKey.includes('started_review') || stepKey.includes('workflow_started')) return 0;
  if (stepKey.includes('standardiz')) return 1;
  if (stepKey.includes('volume') || stepKey.includes('analyz') || stepKey.includes('analysis')) return 3;
  if (stepKey.includes('prescription') || stepKey.includes('trx') || stepKey.includes('retriev') || stepKey.includes('query')) return 2;
  if (stepKey.includes('human') || stepKey.includes('agentic_workflow') || stepKey.includes('agentic workflow') || stepKey.includes('waiting') || stepKey.includes('manual_review') || stepKey.includes('manual review')) return 4;
  return -1;
};

const formatDateTime = (value) => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;

  return date
    .toLocaleString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    })
    .replace(',', '');
};

const getWorkflowState = (state, isReviewStep) => {
  const status = String(state || '').toLowerCase();

  if (['completed', 'complete', 'done', 'approved', 'verified', 'success', 'approve'].includes(status)) {
    return { state: 'completed', status: 'Completed' };
  }
  if (['failed', 'error', 'rejected', 'cancelled'].includes(status)) {
    return { state: 'failed', status: 'Failed' };
  }
  if (['running', 'active', 'in_progress', 'in progress', 'processing'].includes(status)) {
    return { state: 'running', status: 'In Progress' };
  }
  if (
    status.includes('review') ||
    status.includes('pending') ||
    status.includes('awaiting') ||
    status.includes('waiting') ||
    status.includes('manual')
  ) {
    return isReviewStep
      ? { state: 'awaiting_review', status: 'Awaiting Review' }
      : { state: 'idle', status: 'Pending' };
  }

  return { state: 'idle', status: status ? formatTimelineKey(status) : 'Pending' };
};

const getEvidenceState = (label, value) => {
  const normalizedValue = String(value).toLowerCase();
  const normalizedLabel = String(label).toLowerCase();
  const isCheck = ['valid', 'passed', 'yes', 'true', 'completed', 'deliverable', 'success'].includes(normalizedValue);
  const isCross = ['missing', 'no', 'false', 'failed', 'invalid', 'insufficient data', 'unable to validate'].includes(normalizedValue);
  const isBadge =
    normalizedLabel.includes('score') ||
    normalizedLabel.includes('risk') ||
    normalizedLabel.includes('priority') ||
    normalizedLabel.includes('action') ||
    normalizedLabel.includes('status');
  const badgeColor =
    normalizedValue.includes('high') || normalizedValue.includes('no') || normalizedValue.includes('failed')
      ? '#ef4444'
      : normalizedValue.includes('medium') || normalizedValue.includes('review') || normalizedValue.includes('pending')
        ? '#fb923c'
        : '#16a34a';

  return { isCheck, isCross, isBadge, badgeColor };
};

const formatEvidenceValue = (value, label = '') => {
  if (value == null || value === '') return '-';
  let strVal = typeof value === 'object' ? JSON.stringify(value, null, 2) : String(value);
  if (String(label).toLowerCase().includes('query')) {
    strVal = strVal.replace(/LEO/g, 'Goodlabs');
  }
  return strVal;
};

const isClickableEvidence = (label, value) => {
  const normalizedLabel = String(label).toLowerCase();
  return Boolean(
    (value && typeof value === 'object') ||
    normalizedLabel.includes('information') ||
    normalizedLabel.includes('result') ||
    normalizedLabel.includes('search') ||
    normalizedLabel.includes('website') ||
    normalizedLabel.includes('query') ||
    normalizedLabel.includes('url') ||
    normalizedLabel.includes('source')
  );
};

const isPreviewableEvidenceLabel = (label) => {
  const normalizedLabel = String(label).toLowerCase();
  return (
    normalizedLabel === 'query' ||
    normalizedLabel.includes('website') ||
    normalizedLabel.includes('information found') ||
    normalizedLabel.includes('website info') ||
    normalizedLabel.includes('info')
  );
};

const hasDisplayValue = (value) => {
  if (value === undefined || value === null) return false;
  const text = String(value).trim();
  return text !== '' && text !== '-';
};

const refineTimelineStepDisplay = (step) => {
  const title = String(step.title || '').toLowerCase();
  const isPrescriptionRetrievalStep =
    step.isPrescriptionRetrievalStep ||
    title.includes('prescription data retrieval') ||
    title.includes('retrieving prescription') ||
    title.includes('retrieve trx');
  const evidence = Array.isArray(step.evidence)
    ? step.evidence.filter((item) => {
        if (!isPrescriptionRetrievalStep) return true;
        return String(item?.label || '').toLowerCase() !== 'table name';
      })
    : step.evidence;

  return {
    ...step,
    evidence,
    isPrescriptionRetrievalStep,
  };
};

const getEvidenceDialogTitle = (label) => {
  const normalizedLabel = String(label).toLowerCase();
  if (normalizedLabel === 'query') return 'Query Preview';
  if (normalizedLabel.includes('info')) return 'Information Preview';
  return 'Website Preview';
};

const getEvidenceDisplayText = (ev) => {
  if (ev?.label === 'Website') {
    if (ev?.rawValue && typeof ev.rawValue === 'object') {
      return ev.rawValue.website || ev.rawValue.url || ev.rawValue.domain || ev.rawValue.value || ev.value || '-';
    }
    return ev?.value ?? String(ev?.rawValue ?? '-');
  }
  return ev?.value ?? String(ev?.rawValue ?? '-');
};

const renderDialogContent = (content, title = '') => {
  if (content && typeof content === 'object' && !Array.isArray(content)) {
    const website = content.website || content.url || content.domain || '-';
    const info = content.website_info || content.wesite_info || content.info || content.information || '-';

    return (
      <Stack spacing={2}>
        <Box>
          <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', display: 'block', mb: 0.5 }}>
            Website
          </Typography>
          <Typography sx={{ color: '#0f172a', fontSize: '0.9rem', fontWeight: 600, wordBreak: 'break-word' }}>
            {website}
          </Typography>
        </Box>
        <Box>
          <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', display: 'block', mb: 0.5 }}>
            Information
          </Typography>
          <Typography sx={{ color: '#334155', fontSize: '0.85rem', lineHeight: 1.7, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
            {info}
          </Typography>
        </Box>
      </Stack>
    );
  }

  return (
    <Typography
      component="pre"
      sx={{
        m: 0,
        color: '#334155',
        fontSize: '0.8rem',
        lineHeight: 1.75,
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-word',
        fontFamily:
          '"SFMono-Regular", Consolas, "Liberation Mono", Menlo, monospace',
        fontWeight: 500,
      }}
    >
      {(() => {
        let strVal = typeof content === 'string' ? content : content ? JSON.stringify(content, null, 2) : '-';
        if (String(title).toLowerCase().includes('query')) {
          strVal = strVal.replace(/LEO/g, 'Goodlabs');
        }
        return strVal;
      })()}
    </Typography>
  );
};

const flattenEvidenceEntries = (source) =>
  Object.entries(source || {})
    .filter(([key]) => {
      const lowerKey = String(key).toLowerCase();
      return lowerKey !== 'derivation_query' && lowerKey !== 'derivation query';
    })
    .flatMap(([key, value]) => {
      if (value && typeof value === 'object' && !Array.isArray(value)) {
        return Object.entries(value).map(([nestedKey, nestedValue]) => ({
          key: formatTimelineKey(`${key} ${nestedKey}`),
          value: nestedValue,
        }));
      }

      return [{ key: formatTimelineKey(key), value }];
    });

const buildEvidenceRows = (evidence) => {
  if (!evidence) return null;

  if (Array.isArray(evidence)) {
    return evidence.map((item, index) => {
      if (item && typeof item === 'object') {
        const label = item.label || item.name || item.key || `Evidence ${index + 1}`;
        const value = item.value ?? item.status ?? item.result ?? item.message ?? '-';
        return {
          ...item,
          label,
          value: formatEvidenceValue(value, label),
          rawValue: value,
          ...getEvidenceState(label, value),
          isClickable: isClickableEvidence(label, value),
        };
      }

      return {
        label: `Evidence ${index + 1}`,
        value: formatEvidenceValue(item, `Evidence ${index + 1}`),
        rawValue: item,
        ...getEvidenceState(`Evidence ${index + 1}`, item),
        isClickable: isClickableEvidence(`Evidence ${index + 1}`, item),
      };
    });
  }

  if (typeof evidence === 'object') {
    return flattenEvidenceEntries(evidence).map(({ key, value }) => ({
      label: key,
      value: formatEvidenceValue(value, key),
      rawValue: value,
      ...getEvidenceState(key, value),
      isClickable: isClickableEvidence(key, value),
    }));
  }

  return [{ label: 'Evidence', value: formatEvidenceValue(evidence, 'Evidence'), rawValue: evidence, ...getEvidenceState('Evidence', evidence), isClickable: isClickableEvidence('Evidence', evidence) }];
};

const createBlankStep = (template, index, visuals) => ({
  ...(template || {}),
  index: index + 1,
  stepNumber: index + 1,
  status: 'Pending',
  completedAt: '-',
  duration: '-',
  enteredStepAt: '-',
  estimatedReviewTime: '-',
  assignedTo: '-',
  slaDueBy: '-',
  reviewPriority: '-',
  description: '-',
  outcome: '-',
  whatHappensNext: '-',
  notes: '',
  evidenceHeader: template?.evidenceHeader || 'EVIDENCE',
  evidence: (template?.evidence || []).map((ev) => ({
    ...ev,
    value: ev.value ?? '-',
    isCheck: false,
    isCross: false,
    isBadge: false,
    isGreenCircle: false,
    isOrangeWarning: false,
    isPriorityBadge: false,
  })),
  accent: template?.accent || visuals.accent,
  iconComponent: visuals.iconComponent,
  state: 'idle',
});

const normalizeWorkflowStep = (step, index, totalSteps, isAccountChange) => {
  const stepKey = getStepKey(step);
  const config = isAccountChange ? accountChangeStepsConfig : defaultStepsConfig;
  const template = config[getStepTemplateIndex(stepKey, isAccountChange)];
  const visuals = {
    ...getTimelineVisuals(step, index),
    accent: step.accent || getTimelineVisuals(step, index).accent,
    iconComponent: step.iconComponent || step.icon || getTimelineVisuals(step, index).iconComponent,
  };
  const parsedMsg = parseMessagePayload(step.message || step.subtitle || step.description);
  const hasOneKeyId = Boolean(
    step.oneKeyId ||
    step.one_key_id ||
    step.oneKey ||
    step.one_key ||
    parsedMsg?.oneKeyId ||
    parsedMsg?.one_key_id ||
    parsedMsg?.oneKey ||
    parsedMsg?.one_key
  );
  const isReviewStep =
    (stepKey.includes('human') ||
    stepKey.includes('manual') ||
    stepKey.includes('review') ||
    stepKey.includes('waiting') ||
    stepKey.includes('agentic_workflow') ||
    stepKey.includes('agentic workflow')) &&
    !stepKey.includes('human review required');
  const isSkippedStandardization = !isAccountChange && hasOneKeyId && index === 1;
  const stateMeta = isSkippedStandardization
    ? { state: 'completed', status: 'Skipped' }
    : getWorkflowState(step.state || step.status, isReviewStep);
  const base = createBlankStep(template, index, visuals);
  const rawTitle =
    step.step ||
    step.stepName ||
    step.eventName ||
    step.name ||
    step.title ||
    template?.title ||
    `Step ${index + 1}`;
  const evidenceSource = step.evidence || parsedMsg?.evidence;
  const reviewEvidence =
    evidenceSource && typeof evidenceSource === 'object' && !Array.isArray(evidenceSource)
      ? evidenceSource
      : {};
  const evidenceRows = buildEvidenceRows(evidenceSource);
  const isRetrievingPrescriptionStep =
    stepKey.includes('retrieve_trx_data') ||
    stepKey.includes('retrieving prescription') ||
    stepKey.includes('prescription data retrieval') ||
    stepKey.includes('retrieving prescription data');
  const isPrescriptionAnalysisStep =
    stepKey.includes('analyze_trx_data') ||
    stepKey.includes('analyzing prescription') ||
    stepKey.includes('prescription data analysis') ||
    stepKey.includes('prescription volume');
    
  const filteredEvidenceRows = isPrescriptionAnalysisStep
    ? (evidenceRows || []).filter(ev => {
        const lbl = String(ev.label || '').toLowerCase();
        return !lbl.includes('query') && lbl !== 'detailed query';
      })
    : (evidenceRows || []);

  const websiteValue =
    step.website ||
    step.website_info ||
    step.wesite_info ||
    parsedMsg?.website ||
    parsedMsg?.website_info ||
    parsedMsg?.wesite_info ||
    parsedMsg?.domain ||
    parsedMsg?.sourceWebsite;
  const queryValue =
    step.query ||
    step.searchQuery ||
    step.sqlQuery ||
    parsedMsg?.query ||
    parsedMsg?.searchQuery ||
    parsedMsg?.sqlQuery ||
    parsedMsg?.search_query ||
    reviewEvidence.query ||
    reviewEvidence.searchQuery ||
    reviewEvidence.search_query;
  const infoValue =
    step.wesite_info ||
    step.website_info ||
    parsedMsg?.wesite_info ||
    parsedMsg?.website_info ||
    reviewEvidence.wesite_info ||
    reviewEvidence.website_info;
  const websiteEvidenceRows =
    isAccountChange && stepKey.includes('internet_search') && websiteValue && !filteredEvidenceRows.some((ev) => String(ev.label || '').toLowerCase().includes('website'))
      ? [
          {
            label: 'Website',
            value: formatEvidenceValue(websiteValue),
            rawValue: {
              website: websiteValue,
              website_info: infoValue,
            },
            isClickable: true,
            ...getEvidenceState('Website', websiteValue),
          },
        ]
      : [];
  const detailedQueryValue =
    step.detailedQuery ||
    step.detailed_query ||
    step.detailsQuery ||
    parsedMsg?.detailedQuery ||
    parsedMsg?.detailed_query ||
    parsedMsg?.detailsQuery ||
    reviewEvidence.detailedQuery ||
    reviewEvidence.detailed_query ||
    reviewEvidence.detailsQuery;
  const evidenceWithQuery =
    isRetrievingPrescriptionStep && queryValue && !filteredEvidenceRows.some((ev) => ev.label.toLowerCase().includes('query'))
      ? [
          ...filteredEvidenceRows,
          ...websiteEvidenceRows,
          {
            label: 'Query',
            value: formatEvidenceValue(queryValue),
            rawValue: queryValue,
            isClickable: true,
            ...getEvidenceState('Query', queryValue),
          },
          ...(detailedQueryValue ? [{
            label: 'Detailed Query',
            value: formatEvidenceValue(detailedQueryValue),
            rawValue: detailedQueryValue,
            isClickable: true,
            ...getEvidenceState('Detailed Query', detailedQueryValue),
          }] : [])
        ]
      : [...filteredEvidenceRows, ...websiteEvidenceRows];
  const timeValue = step.completedAt || step.updated_at || step.updatedAt || step.timestamp || parsedMsg?.completedAt;
  const formattedTime = formatDateTime(timeValue);
  const createdTime = Date.parse(step.created_at || step.createdAt || '');
  const updatedTime = Date.parse(step.updated_at || step.updatedAt || step.timestamp || '');

  return {
    ...base,
    ...stateMeta,
    title: formatTimelineKey(stripStepNumber(rawTitle)) || formatTimelineKey(stepKey || `Step ${index + 1}`),
    description:
      (isSkippedStandardization && index === 1
        ? 'Address standardization was skipped because a OneKey ID was present for this request.'
        : (() => {
            const desc = step.description ||
              parsedMsg?.summary ||
              parsedMsg?.reason ||
              parsedMsg?.description ||
              parsedMsg?.error_message ||
              parsedMsg?.message ||
              step.subtitle ||
              step.message ||
              'Timeline event received from workflow service.';
            return Array.isArray(desc) ? desc.join(' ') : desc;
          })()),
    outcome: isSkippedStandardization
      ? 'The request already had a valid OneKey ID, so address standardization was skipped.'
      : step.outcome || parsedMsg?.outcome || parsedMsg?.reason || base.outcome,
    completedAt: isReviewStep ? base.completedAt : formattedTime || base.completedAt,
    enteredStepAt: isReviewStep ? formattedTime || base.enteredStepAt : base.enteredStepAt,
    duration:
      step.duration ||
      parsedMsg?.duration ||
      (!Number.isNaN(createdTime) && !Number.isNaN(updatedTime) && updatedTime > createdTime
        ? `${((updatedTime - createdTime) / 1000).toFixed(1)} sec`
        : base.duration),
    assignedTo: step.assignedTo || parsedMsg?.assignedTo || reviewEvidence.assignedTo || base.assignedTo,
    slaDueBy: step.slaDueBy || parsedMsg?.slaDueBy || reviewEvidence.slaDueBy || base.slaDueBy,
    reviewPriority: step.reviewPriority || parsedMsg?.reviewPriority || reviewEvidence.reviewPriority || base.reviewPriority,
    estimatedReviewTime:
      step.estimatedReviewTime ||
      parsedMsg?.estimatedReviewTime ||
      reviewEvidence.estimatedReviewTime ||
      base.estimatedReviewTime,
    whatHappensNext: step.whatHappensNext || parsedMsg?.whatHappensNext || base.whatHappensNext,
    notes: step.notes || parsedMsg?.notes || base.notes,
    evidence: evidenceWithQuery || base.evidence,
    isReviewStep,
    isEvidenceSummaryMode: isReviewStep && !evidenceWithQuery ? base.isEvidenceSummaryMode : false,
    isInternetSearchStep: stepKey.includes('internet_search') || stepKey.includes('internet search') || stepKey.includes('web_search') || stepKey.includes('web search') || String(rawTitle).toLowerCase().includes('internet search'),
    isAccountChange,
    // Populate address meta from evidenceSource for address change, or flat evidence fields for account change
    currentAddressMeta: isAccountChange
      ? {
          matchCount: (() => { const v = reviewEvidence?.match_count !== undefined ? reviewEvidence.match_count : reviewEvidence?.match; return v !== undefined ? (v === true ? 'Yes' : v === false ? 'No' : String(v)) : '-'; })(),
          confidence: reviewEvidence?.confidence !== undefined ? reviewEvidence.confidence : '-',
          sources: reviewEvidence?.source ? [reviewEvidence.source] : '-',
        }
      : {
          matchCount: reviewEvidence?.matched_current_primary?.count ?? parsedMsg?.old_rank ?? parsedMsg?.currentAddressMeta?.matchCount ?? '-',
          confidence: reviewEvidence?.matched_current_primary?.confidence ?? (parsedMsg?.old_trx !== undefined ? Number(parsedMsg.old_trx).toFixed(2) : parsedMsg?.currentAddressMeta?.confidence ?? '-'),
          sources: reviewEvidence?.matched_current_primary?.sources ?? parsedMsg?.source ?? parsedMsg?.currentAddressMeta?.sources ?? '-',
        },
    requestAddressMeta: isAccountChange
      ? null
      : {
          matchCount: reviewEvidence?.matched_requested_address?.count ?? parsedMsg?.new_rank ?? parsedMsg?.requestAddressMeta?.matchCount ?? '-',
          confidence: reviewEvidence?.matched_requested_address?.confidence ?? (parsedMsg?.new_trx !== undefined ? Number(parsedMsg.new_trx).toFixed(2) : parsedMsg?.requestAddressMeta?.confidence ?? '-'),
          sources: reviewEvidence?.matched_requested_address?.sources ?? parsedMsg?.source ?? parsedMsg?.requestAddressMeta?.sources ?? '-',
        },
  };
};

const WorkflowTimelineBottomBar = ({
  workflowSteps = [],
  agentStatuses = [],
  timelineUsesMockData = false,
  agentOverallStatus = null,
  timelineLastUpdated = null,
}) => {
  const [timelineExpanded, setTimelineExpanded] = useState(true);
  const [selectedStepIdx, setSelectedStepIdx] = useState(null);
  const [detailDialog, setDetailDialog] = useState({ open: false, title: '', content: null });
  const [copiedDialogContent, setCopiedDialogContent] = useState(false);

  const openEvidenceDialog = (title, content) => {
    setDetailDialog({ open: true, title, content });
  };

  const closeEvidenceDialog = () => {
    setDetailDialog({ open: false, title: '', content: null });
  };

  const copyDialogContent = async () => {
    const content =
      typeof detailDialog.content === 'string'
        ? detailDialog.content
        : JSON.stringify(detailDialog.content ?? '-', null, 2);

    try {
      await navigator.clipboard.writeText(content);
      setCopiedDialogContent(true);
      window.setTimeout(() => setCopiedDialogContent(false), 1200);
    } catch {
      setCopiedDialogContent(false);
    }
  };

  // Map API workflow steps to standard steps
  const steps = useMemo(() => {
    const sourceSteps = workflowSteps.length > 0 ? workflowSteps : agentStatuses;
    // Detect workflow type.
    // Address change is identified by: dcr_type === 'address_change' on any step, OR
    // presence of prescription/standardization steps.
    // Account change is identified only when it is NOT an address change workflow.
    const isAddressChange = sourceSteps.some((s) => {
      const dcrType = String(s.dcr_type || '').toLowerCase();
      const k = String(s.step || s.stepName || s.name || '').toLowerCase();
      return dcrType === 'address_change' ||
        k.includes('prescription') || k.includes('standardiz') ||
        k.includes('retriev') || k.includes('trx') || k.includes('analysis');
    });
    const isAccountChange = !isAddressChange && sourceSteps.some((s) => {
      const k = String(s.step || s.stepName || s.name || '').toLowerCase();
      return k.includes('internet_search') || k.includes('internet search') || k.includes('search_validation') || k.includes('manual_review');
    });

    const baseConfig = isAccountChange ? accountChangeStepsConfig : defaultStepsConfig;

    const blankOutFallbackStep = (step) => ({
      ...step,
      status: '-',
      completedAt: '-',
      duration: '-',
      enteredStepAt: '-',
      estimatedReviewTime: '-',
      assignedTo: '-',
      slaDueBy: '-',
      reviewPriority: '-',
      description: '-',
      outcome: '-',
      whatHappensNext: '-',
      notes: '',
      evidence: (step.evidence || []).map((ev) => ({
        ...ev,
        value: '-',
        isCheck: false,
        isCross: false,
        isBadge: false,
        isGreenCircle: false,
        isOrangeWarning: false,
        isPriorityBadge: false,
      })),
    });

    const slots = JSON.parse(JSON.stringify(baseConfig)).map(blankOutFallbackStep);

    if (isAccountChange) {
      // Account-change icons
      slots[0].iconComponent = StreamRoundedIcon;
      slots[1].iconComponent = PublicRoundedIcon;
      slots[2].iconComponent = ShieldRoundedIcon;
      slots[3].iconComponent = PsychologyAltRoundedIcon;
      slots[4].iconComponent = HourglassTopRoundedIcon;
    } else {
      // Address-change icons
      slots[0].iconComponent = StreamRoundedIcon;
      slots[1].iconComponent = BoltIcon;
      slots[2].iconComponent = PsychologyAltRoundedIcon;
      slots[3].iconComponent = PublicRoundedIcon;
      slots[4].iconComponent = HourglassTopRoundedIcon;
    }

    if (timelineUsesMockData) {
      return slots;
    }

    if (!sourceSteps || sourceSteps.length === 0) {
      return [];
    }

    // Map incoming steps to slots dynamically
    const mappedSlots = sourceSteps.map((step, index) => {
      const stepKey = String(step.step || step.stepName || step.eventName || step.name || step.title || '').toLowerCase();
      let slotIdx = -1;

      if (isAccountChange) {
        // Account-change slot mapping
        if (stepKey.includes('steward_agent') || stepKey.includes('steward agent') || stepKey.includes('steward_agent_workflow')) {
          slotIdx = 0;
        } else if (stepKey.includes('internet_search') || stepKey.includes('internet search')) {
          slotIdx = 1;
        } else if (stepKey.includes('search_validation') || stepKey.includes('search validation')) {
          slotIdx = 2;
        } else if (stepKey.includes('manual_review') || stepKey.includes('manual review')) {
          slotIdx = 3;
        } else if (stepKey.includes('agentic_workflow') || stepKey.includes('agentic workflow') || stepKey.includes('human') || stepKey.includes('waiting')) {
          slotIdx = 4;
        }
      } else {
        // Address-change slot mapping
        if (stepKey.includes('steward_agent') || stepKey.includes('steward agent') || stepKey.includes('started review') || stepKey.includes('started_review') || stepKey.includes('workflow_started') || stepKey.includes('steward_agent_workflow')) {
          slotIdx = 0;
        } else if (stepKey.includes('standardiz')) {
          slotIdx = 1;
        } else if (stepKey.includes('volume') || stepKey.includes('analyz') || stepKey.includes('analysis')) {
          slotIdx = 3;
        } else if (stepKey.includes('prescription') || stepKey.includes('trx') || stepKey.includes('retriev') || stepKey.includes('query')) {
          slotIdx = 2;
        } else if (stepKey.includes('recommendation') || stepKey.includes('human review required') || stepKey.includes('human') || stepKey.includes('agentic_workflow') || stepKey.includes('agentic workflow') || stepKey.includes('waiting') || stepKey.includes('manual_review') || stepKey.includes('manual review')) {
          slotIdx = 4;
        }
      }

      if (slotIdx >= 0 && slotIdx < slots.length) {
        const slot = {
          ...slots[slotIdx],
          evidence: (slots[slotIdx].evidence || []).map((ev) => ({ ...ev })),
        };
        const incomingTitle = formatTimelineKey(
          stripStepNumber(
            step.step ||
            step.stepName ||
            step.eventName ||
            step.name ||
            step.title ||
            slot.title
          )
        );
        const statusVal = String(
          step.state || step.status || (slotIdx === slots.length - 1 ? 'awaiting_review' : '')
        ).toLowerCase();

        // Map visual state
        if (['completed', 'complete', 'done', 'approved', 'verified', 'success'].includes(statusVal)) {
          slot.state = 'completed';
          slot.status = 'Completed';
        } else if (['failed', 'error', 'rejected'].includes(statusVal)) {
          slot.state = 'failed';
          slot.status = 'Failed';
        } else if (['running', 'active', 'in_progress', 'in progress'].includes(statusVal)) {
          slot.state = 'running';
          slot.status = 'In Progress';
        } else if (statusVal.includes('review') || statusVal.includes('pending') || statusVal.includes('awaiting') || statusVal.includes('waiting') || statusVal.includes('manual')) {
          slot.state = 'awaiting_review';
          slot.status = 'Awaiting Review';
        } else {
          slot.state = 'idle';
          slot.status = 'Pending';
        }

        if (incomingTitle) {
          slot.title = incomingTitle;
        }

        // Read assignedTo / slaDueBy / reviewPriority from the step root OR step.evidence
        const ev7 = (step.evidence && typeof step.evidence === 'object' && !Array.isArray(step.evidence)) ? step.evidence : {};
        slot.assignedTo = step.assignedTo || ev7.assignedTo || slot.assignedTo;
        slot.slaDueBy = step.slaDueBy || ev7.slaDueBy || slot.slaDueBy;
        slot.reviewPriority = step.reviewPriority || ev7.reviewPriority || slot.reviewPriority;
        slot.estimatedReviewTime = step.estimatedReviewTime || ev7.estimatedReviewTime || slot.estimatedReviewTime;

        // Parse details if message payload exists
        if (step.subtitle || step.description || step.message || step.outcome || step.duration || step.completedAt || step.evidence || step.assignedTo) {
          const rawMessage = step.message || step.subtitle || step.description;
          let parsedMsg = null;
          if (rawMessage) {
            try {
              parsedMsg = typeof rawMessage === 'string' ? JSON.parse(rawMessage) : rawMessage;
            } catch {
              parsedMsg = null;
            }
          }

          slot.description = (() => {
            const desc = step.description ||
              parsedMsg?.summary ||
              parsedMsg?.reason ||
              parsedMsg?.description ||
              parsedMsg?.error_message ||
              parsedMsg?.message ||
              step.subtitle ||
              rawMessage ||
              '-';
            return Array.isArray(desc) ? desc.join(' ') : desc;
          })();
          slot.outcome = step.outcome || parsedMsg?.outcome || parsedMsg?.reason || slot.outcome;
          slot.isInternetSearchStep = stepKey.includes('internet_search') || stepKey.includes('internet search') || stepKey.includes('web_search') || stepKey.includes('web search') || String(rawMessage).toLowerCase().includes('internet search');
          slot.isAccountChange = isAccountChange;
          
          let evidenceObjExtracted = step.evidence || parsedMsg?.evidence;
          if (typeof evidenceObjExtracted === 'string') {
            try {
              evidenceObjExtracted = JSON.parse(evidenceObjExtracted);
            } catch (e) {}
          }

          // For account change, populate currentAddressMeta from flat evidence fields (source/match/confidence)
          if (isAccountChange && slot.isInternetSearchStep) {
            const matchVal = evidenceObjExtracted?.match_count !== undefined ? evidenceObjExtracted.match_count : evidenceObjExtracted?.match;
            const confVal = evidenceObjExtracted?.confidence;
            const srcVal = evidenceObjExtracted?.source;
            slot.currentAddressMeta = {
              matchCount: matchVal !== undefined ? (matchVal === true ? 'Yes' : matchVal === false ? 'No' : String(matchVal)) : '-',
              confidence: confVal !== undefined ? confVal : '-',
              sources: srcVal ? [srcVal] : '-',
            };
            slot.requestAddressMeta = null;
          } else {
            slot.currentAddressMeta = {
              matchCount: evidenceObjExtracted?.matched_current_primary?.count ?? step.matched_current_primary?.count ?? parsedMsg?.old_rank ?? parsedMsg?.currentAddressMeta?.matchCount ?? '-',
              confidence: evidenceObjExtracted?.matched_current_primary?.confidence ?? step.matched_current_primary?.confidence ?? (parsedMsg?.old_trx !== undefined ? Number(parsedMsg.old_trx).toFixed(2) : parsedMsg?.currentAddressMeta?.confidence ?? '-'),
              sources: evidenceObjExtracted?.matched_current_primary?.sources ?? step.matched_current_primary?.sources ?? parsedMsg?.source ?? parsedMsg?.currentAddressMeta?.sources ?? '-',
            };
            slot.requestAddressMeta = {
              matchCount: evidenceObjExtracted?.matched_requested_address?.count ?? step.matched_requested_address?.count ?? parsedMsg?.new_rank ?? parsedMsg?.requestAddressMeta?.matchCount ?? '-',
              confidence: evidenceObjExtracted?.matched_requested_address?.confidence ?? step.matched_requested_address?.confidence ?? (parsedMsg?.new_trx !== undefined ? Number(parsedMsg.new_trx).toFixed(2) : parsedMsg?.requestAddressMeta?.confidence ?? '-'),
              sources: evidenceObjExtracted?.matched_requested_address?.sources ?? step.matched_requested_address?.sources ?? parsedMsg?.source ?? parsedMsg?.requestAddressMeta?.sources ?? '-',
            };
          }

          // Complete At / Entered Step At
          const timeValue = step.completedAt || step.updated_at || step.updatedAt || step.timestamp || parsedMsg?.completedAt;
          if (timeValue) {
            const d = new Date(timeValue);
            if (!isNaN(d.getTime())) {
              const formattedTime = d.toLocaleString('en-US', {
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
                hour: '2-digit',
                minute: '2-digit',
                hour12: true
              }).replace(',', '');

              if (slotIdx === 6) {
                slot.enteredStepAt = formattedTime;
              } else {
                slot.completedAt = formattedTime;
              }
            }
          }

          // Duration
          const directDuration = step.duration || parsedMsg?.duration;
          if (directDuration) {
            slot.duration = directDuration;
          } else {
            const createdTime = Date.parse(step.created_at || step.createdAt || '');
            const updatedTime = Date.parse(step.updated_at || step.updatedAt || step.timestamp || '');
            if (!isNaN(createdTime) && !isNaN(updatedTime) && updatedTime > createdTime) {
              slot.duration = `${((updatedTime - createdTime) / 1000).toFixed(1)} sec`;
            }
          }



          // Dynamic evidence mapping from step.evidence or parsedMsg.evidence
          // For last slot, strip the metadata keys that have already been assigned to slot fields
          let evidenceObj = step.evidence || parsedMsg?.evidence;
          if (slotIdx === slots.length - 1 && evidenceObj && typeof evidenceObj === 'object' && !Array.isArray(evidenceObj)) {
            const { assignedTo: _a, slaDueBy: _s, reviewPriority: _r, estimatedReviewTime: _e, ...restEvidence } = evidenceObj;
            evidenceObj = Object.keys(restEvidence).length > 0 ? restEvidence : null;
          }
          if (evidenceObj && typeof evidenceObj === 'object') {
            const isLabelMatch = (l1, l2) => {
              const s1 = String(l1).toLowerCase().replace(/[^a-z0-9]/g, '');
              const s2 = String(l2).toLowerCase().replace(/[^a-z0-9]/g, '');
              return s1 === s2 || s1.includes(s2) || s2.includes(s1);
            };
            const flattenEvidenceEntries = (source) =>
              Object.entries(source || {})
                .filter(([key]) => {
                  const lowerKey = String(key).toLowerCase();
                  if (lowerKey === 'derivation_query' || lowerKey === 'derivation query') return false;
                  
                  const stepKeyStr = String(step.step || step.stepName || step.eventName || step.name || step.title || '').toLowerCase();
                  const isPrescriptionAnalysisStep =
                    stepKeyStr.includes('analyze_trx_data') ||
                    stepKeyStr.includes('analyzing prescription') ||
                    stepKeyStr.includes('prescription data analysis') ||
                    stepKeyStr.includes('prescription volume');
                    
                  if (isPrescriptionAnalysisStep && (lowerKey === 'query' || lowerKey === 'detailed_query' || lowerKey === 'detailed query' || lowerKey === 'search_query' || lowerKey === 'searchquery')) {
                    return false;
                  }
                  return true;
                })
                .flatMap(([key, val]) => {
                  if (val && typeof val === 'object' && !Array.isArray(val)) {
                    return Object.entries(val).map(([nestedKey, nestedVal]) => {
                      const combinedKey = `${key} ${nestedKey}`;
                      const formattedKey = formatTimelineKey(combinedKey);
                      return {
                        key: formattedKey,
                        value: nestedVal,
                        matchKeys: [key, nestedKey, combinedKey, formattedKey],
                      };
                    });
                  }

                  const formattedKey = formatTimelineKey(key);
                  return [{ key: formattedKey, value: val, matchKeys: [key, formattedKey] }];
                })
                .filter((entry) => {
                  const stepKeyStr = String(step.step || step.stepName || step.eventName || step.name || step.title || '').toLowerCase();
                  const isPrescriptionAnalysisStep =
                    stepKeyStr.includes('analyze_trx_data') ||
                    stepKeyStr.includes('analyzing prescription') ||
                    stepKeyStr.includes('prescription data analysis') ||
                    stepKeyStr.includes('prescription volume');
                    
                  if (isPrescriptionAnalysisStep && (entry.key === 'Current Zip' || entry.key === 'Requested Zip')) {
                    return false;
                  }
                  return true;
                });
            const getEvidenceState = (label, val) => {
              const normalizedValue = String(val).toLowerCase();
              const normalizedLabel = String(label).toLowerCase();
              const isCheck = ['valid', 'passed', 'yes', 'true', 'completed', 'deliverable'].includes(normalizedValue);
              const isCross = ['missing', 'no', 'false', 'failed', 'invalid', 'insufficient data', 'unable to validate'].includes(normalizedValue);
              const isBadge =
                normalizedLabel.includes('score') ||
                normalizedLabel.includes('risk') ||
                normalizedLabel.includes('priority') ||
                normalizedLabel.includes('action');
              const badgeColor =
                normalizedValue.includes('high') || normalizedValue.includes('no')
                  ? '#ef4444'
                  : normalizedValue.includes('medium') || normalizedValue.includes('review')
                    ? '#fb923c'
                    : '#16a34a';

              return { isCheck, isCross, isBadge, badgeColor };
            };
            const evidenceEntries = flattenEvidenceEntries(evidenceObj);

            const hasMatches = (slot.evidence || []).some((ev) =>
              evidenceEntries.some((entry) =>
                entry.matchKeys.some((key) => isLabelMatch(ev.label, key))
              )
            );

            if (!hasMatches) {
              // If none of the keys match, clear defaults and use the new ones entirely
              slot.evidence = evidenceEntries.map(({ key, value }) => {
                const evidenceState = getEvidenceState(key, value);
                return {
                  label: key,
                  value: String(value ?? '-'),
                  ...evidenceState,
                };
              });
              slot.isEvidenceSummaryMode = false;
            } else {
              // Otherwise, update matches and append new ones
              const mappedLabels = new Set();
              slot.evidence = (slot.evidence || []).map((ev) => {
                const matchedEntry = evidenceEntries.find((entry) =>
                  entry.matchKeys.some((key) => isLabelMatch(ev.label, key))
                );
                if (matchedEntry) {
                  mappedLabels.add(matchedEntry.key);
                  const evidenceState = getEvidenceState(matchedEntry.key, matchedEntry.value);
                  return {
                    ...ev,
                    value: String(matchedEntry.value ?? '-'),
                    ...evidenceState,
                  };
                }
                return ev;
              });

              evidenceEntries.forEach(({ key, value }) => {
                if (!mappedLabels.has(key)) {
                  const evidenceState = getEvidenceState(key, value);
                  slot.evidence.push({
                    label: key,
                    value: String(value ?? '-'),
                    ...evidenceState,
                  });
                }
              });
            }
          }

          if (slotIdx === 2 && !isAccountChange) {
            const queryValue =
              step.query ||
              step.searchQuery ||
              step.sqlQuery ||
              step.search_query ||
              parsedMsg?.query ||
              parsedMsg?.searchQuery ||
              parsedMsg?.sqlQuery ||
              parsedMsg?.search_query;
            const queryLabelMatch = (label) => String(label || '').toLowerCase() === 'query';
            slot.isPrescriptionRetrievalStep = true;
            slot.evidence = (slot.evidence || []).filter((ev) => String(ev.label || '').toLowerCase() !== 'table name');

            if (hasDisplayValue(queryValue)) {
              const existingQuery = slot.evidence.find((ev) => queryLabelMatch(ev.label));
              if (existingQuery) {
                existingQuery.value = String(queryValue);
                existingQuery.rawValue = queryValue;
                existingQuery.isClickable = false;
              } else {
                slot.evidence.push({
                  label: 'Query',
                  value: String(queryValue),
                  rawValue: queryValue,
                  isClickable: false,
                  isCheck: false,
                  isCross: false,
                  isBadge: false,
                });
              }
            }
          }

          // Fallback legacy inline mapping
          if (parsedMsg) {
            if (slotIdx === 1 && (!evidenceObj || Object.keys(evidenceObj).length === 0)) { // Standardizing (address-change only)
              const oldAddr = [parsedMsg.old_addressline1, parsedMsg.old_addressline2, parsedMsg.old_cityNm, parsedMsg.old_stcd, parsedMsg.old_zip].filter(Boolean).join(', ');
              const newAddr = [parsedMsg.new_addressline1, parsedMsg.new_addressline2, parsedMsg.new_cityNm, parsedMsg.new_stcd, parsedMsg.new_zip].filter(Boolean).join(', ');
              const score = parsedMsg.matchScore || parsedMsg.score || parsedMsg.match_score;
              if (oldAddr) slot.evidence[0].value = oldAddr;
              if (newAddr) slot.evidence[1].value = newAddr;
              if (score != null) slot.evidence[2].value = String(score).endsWith('%') ? score : `${score}%`;
            } else if (slotIdx === 2 && (!evidenceObj || Object.keys(evidenceObj).length === 0)) { // Comparing (address-change only)
              if (parsedMsg.sourcesQueried != null) slot.evidence[0].value = String(parsedMsg.sourcesQueried);
              const score = parsedMsg.bestMatchScore || parsedMsg.best_match_score || parsedMsg.matchScore || parsedMsg.match_score;
              if (score != null) slot.evidence[1].value = String(score).endsWith('%') ? score : `${score}%`;
              if (parsedMsg.possibleMatches != null) slot.evidence[2].value = String(parsedMsg.possibleMatches);
            } else if (slotIdx === 3 && (!evidenceObj || Object.keys(evidenceObj).length === 0) && !isAccountChange) { // Verifying (address-change only)
              if (parsedMsg.verificationSource) slot.evidence[0].value = parsedMsg.verificationSource;
              if (parsedMsg.deliverability) {
                slot.evidence[1].value = parsedMsg.deliverability;
                slot.evidence[1].isCheck = ['deliverable', 'valid', 'passed', 'yes', 'true'].includes(String(parsedMsg.deliverability).toLowerCase());
              }
              if (parsedMsg.addressExists) {
                slot.evidence[2].value = parsedMsg.addressExists;
                slot.evidence[2].isCheck = ['yes', 'true', 'valid', 'passed'].includes(String(parsedMsg.addressExists).toLowerCase());
              }
              const score = parsedMsg.verificationScore || parsedMsg.verification_score || parsedMsg.score;
              if (score != null) slot.evidence[3].value = String(score).endsWith('%') ? score : `${score}%`;
            } else if (slotIdx === 4 && (!evidenceObj || Object.keys(evidenceObj).length === 0) && !isAccountChange) { // Analyzing Prescription (address-change only)
              if (parsedMsg.xptSource !== undefined) {
                slot.evidenceHeader = 'EVIDENCE REVIEWED';
                slot.description = 'Prescription volume data is not available for either address. The system analyzed prescription activity for both the current and requested addresses but found insufficient data to determine meaningful comparison.';
                slot.outcome = 'Address change requires human review due to insufficient prescription evidence.';
                slot.evidence = [
                  { label: 'Source', value: 'No prescription volume found', isCheck: true }
                ];

                // Update Source value
                slot.evidence[0].value = parsedMsg.xptSource;
                slot.evidence[0].isCheck = ['valid', 'passed', 'sufficient', 'no prescription volume found'].includes(String(parsedMsg.xptSource).toLowerCase());
                slot.evidence[0].isCross = !slot.evidence[0].isCheck;
              }
            } else if (slotIdx === slots.length - 1) { // Last slot = human review (both workflow types)
              if (parsedMsg.assignedTo) slot.assignedTo = parsedMsg.assignedTo;
              if (parsedMsg.slaDueBy) slot.slaDueBy = parsedMsg.slaDueBy;
              if (parsedMsg.reviewPriority) slot.reviewPriority = parsedMsg.reviewPriority;
            }
          }
        }
        return slot;
      }

      return normalizeWorkflowStep(step, index, sourceSteps.length, isAccountChange);
    });

    return mappedSlots.map(refineTimelineStepDisplay);
  }, [workflowSteps, agentStatuses, timelineUsesMockData]);

  // Determine which step is effectively selected on open
  const lastCompletedIdx = useMemo(() => {
    return steps.reduceRight(
      (found, step, idx) => (found === -1 && step.state === 'completed' ? idx : found),
      -1
    );
  }, [steps]);

  const effectiveStepIdx = selectedStepIdx !== null ? selectedStepIdx : (lastCompletedIdx >= 0 ? lastCompletedIdx : 0);
  const selectedStep = steps[effectiveStepIdx] ?? null;
  

  const hasSteps = steps.length > 0;

  if (!hasSteps) {
    return null;
  }


  // Helper function to render a row with label on left, value on right
  const renderFieldRow = (label, valueComponent) => (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        width: '100%',
      }}
    >
      <Typography
        variant="caption"
        sx={{
          color: '#94a3b8',
          fontSize: '0.72rem',
          textTransform: 'uppercase',
          fontWeight: 600,
        }}
      >
        {label}
      </Typography>
      {typeof valueComponent === 'string' ? (
        <Typography
          variant="body2"
          sx={{
            fontWeight: 600,
            color: '#334155',
            fontSize: '0.82rem',
            textAlign: 'right',
            wordBreak: 'break-word',
          }}
        >
          {valueComponent}
        </Typography>
      ) : (
        valueComponent
      )}
    </Box>
  );  

  return (
    <Box
      sx={{
        backgroundColor: '#fff',
        borderTop: '1px solid #e2e8f0',
        boxShadow: '0 -4px 12px rgba(0, 0, 0, 0.05)',
        transition: 'all 0.3s ease',
        borderLeft: '1px solid #e2e8f0',
      }}
    >
      {/* Header bar */}
      <Box
        onClick={() => hasSteps && setTimelineExpanded(!timelineExpanded)}
        sx={{
          px: 3,
          py: 1.5,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderLeft: hasSteps ? '1px solid #e2e8f0' : 'none',
          cursor: hasSteps ? 'pointer' : 'default',
          '&:hover': hasSteps ? { backgroundColor: '#f8fafc' } : {},
        }}
      >
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#0f172a' }}>
            DCR Workflow & Decision Timeline
          </Typography>
          <Badge
            badgeContent={`${steps.length} steps`}
            sx={{
              '& .MuiBadge-badge': {
                backgroundColor: '#f1f5f9',
                color: '#64748b',
                position: 'static',
                transform: 'none',
                fontWeight: 600,
                px: 1,
                borderRadius: '12px',
              },
            }}
          />
        </Stack>

        <Stack direction="row" spacing={1.5} alignItems="center">
          {agentOverallStatus === 'FAILED' && (
            <Chip
              icon={
                <Box
                  sx={{
                    width: 6,
                    height: 6,
                    borderRadius: '50%',
                    backgroundColor: '#dc2626',
                    boxShadow: '0 0 0 4px rgba(220,38,38,0.12)',
                  }}
                />
              }
              label="FAILED"
              size="small"
              sx={{
                backgroundColor: '#fef2f2',
                color: '#dc2626',
                fontWeight: 700,
                height: 22,
                '& .MuiChip-label': { px: 1, lineHeight: 1 },
                '& .MuiChip-icon': { ml: '8px', mr: '-4px' }
              }}
            />
          )}
          {agentOverallStatus === 'AWAITING_REVIEW' && (
            <Chip
              icon={
                <Box
                  sx={{
                    width: 6,
                    height: 6,
                    borderRadius: '50%',
                    backgroundColor: '#c2410c',
                    boxShadow: '0 0 0 4px rgba(194,65,12,0.12)',
                  }}
                />
              }
              label="AWAITING REVIEW"
              size="small"
              sx={{
                backgroundColor: '#fff7ed',
                color: '#c2410c',
                fontWeight: 700,
                height: 22,
                '& .MuiChip-label': { px: 1, lineHeight: 1 },
                '& .MuiChip-icon': { ml: '8px', mr: '-4px' }
              }}
            />
          )}
          {agentOverallStatus === 'COMPLETED' && (
            <Chip
              icon={
                <Box
                  sx={{
                    width: 6,
                    height: 6,
                    borderRadius: '50%',
                    backgroundColor: '#16a34a',
                    boxShadow: '0 0 0 4px rgba(22,163,74,0.12)',
                  }}
                />
              }
              label="COMPLETED"
              size="small"
              sx={{
                backgroundColor: '#dcfce7',
                color: '#16a34a',
                fontWeight: 700,
                height: 22,
                '& .MuiChip-label': { px: 1, lineHeight: 1 },
                '& .MuiChip-icon': { ml: '8px', mr: '-4px' }
              }}
            />
          )}
          {/* {timelineLastUpdated && (
            <Typography variant="caption" sx={{ color: '#64748b' }}>
              Last updated: {new Date(timelineLastUpdated).toLocaleTimeString()}
            </Typography>
          )} */}
          <IconButton size="small" sx={{ p: 0, color: '#64748b' }} disabled={!hasSteps}>
            {timelineExpanded ? <KeyboardArrowUpRoundedIcon /> : <KeyboardArrowDownRoundedIcon />}
          </IconButton>
        </Stack>
      </Box>

      {/* Timeline and Panel Area */}
      <Collapse in={timelineExpanded}>
        <Box sx={{ px: 3, py: 2, borderTop: '1px solid #e2e8f0', borderLeft: '1px solid #e2e8f0' }}>
          {/* Horizontal Step Bubbles - Stretched to occupy full space */}
          <Stack direction="row" spacing={0} alignItems="flex-start" sx={{ width: '100%', pb: 2, pt: 1, position: 'relative' }}>
            {steps.map((step, idx) => {
              const isRunning = step.state === 'running';
              const isCompleted = step.state === 'completed';
              const isAwaitingReview = step.state === 'awaiting_review';
              const isSelected = effectiveStepIdx === idx;
              const accent = step.accent;

              // Node visual coloring based on state
              const isIdle = step.state === 'idle';
              const isFailed = step.state === 'failed';
              const effectiveAccent = isFailed ? '#ef4444' : accent;
              const nodeColor = isFailed ? '#b91c1c' : isIdle ? '#94a3b8' : accent;
              const iconBackground = isFailed
                ? alpha('#ef4444', 0.08)
                : isIdle
                ? alpha('#cbd5e1', 0.28)
                : isRunning
                ? alpha(accent, 0.2)
                : alpha(accent, 0.1);
              const iconBorder = isFailed
                ? alpha('#ef4444', 0.32)
                : isIdle
                ? alpha('#94a3b8', 0.32)
                : isRunning
                ? alpha(accent, 0.55)
                : alpha(accent, 0.18);

              const Icon = step.iconComponent;

              return (
                <Box
                  key={idx}
                  onClick={(e) => { e.stopPropagation(); setSelectedStepIdx(idx); }}
                  sx={{
                    position: 'relative',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    flex: '1 1 0px',
                    minWidth: 0,
                    cursor: 'pointer',
                    borderRadius: 2,
                    py: 0.5,
                    transition: 'background 0.18s',
                    '&:hover': { backgroundColor: alpha('#1e88e5', 0.04) },
                  }}
                >
                  {/* Step Number Above Avatar */}
                  <Typography sx={{ fontSize: '0.72rem', color: '#94a3b8', fontWeight: 700, mb: 0.75 }}>
                    {idx + 1}
                  </Typography>

                  {/* Relative container for Avatar and Line */}
                  <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', mb: 1.25 }}>
                    {/* Connector Line to Next Step */}
                    {idx !== steps.length - 1 && (
                      <Box
                        sx={{
                          position: 'absolute',
                          top: 22,
                          left: '65%',
                          width: '60%',
                          height: 2,
                          background: isIdle
                            ? `linear-gradient(90deg, ${alpha('#cbd5e1', 0.9)} 0%, ${alpha('#e2e8f0', 0.5)} 100%)`
                            : `linear-gradient(90deg, ${alpha(effectiveAccent, isCompleted ? 0.62 : 0.78)} 0%, ${alpha(effectiveAccent, 0.12)} 100%)`,
                          zIndex: 0,
                          borderRadius: 999,
                        }}
                      />
                    )}

                    {/* Circle Node */}
                    <Avatar
                      sx={{
                        width: 44,
                        height: 44,
                        bgcolor: isSelected ? alpha('#1e88e5', 0.1) : iconBackground,
                        color: nodeColor,
                        border: '1px solid',
                        borderColor: isSelected ? '#1e88e5' : iconBorder,
                        boxShadow: isSelected
                          ? `0 0 0 3px ${alpha('#1e88e5', 0.22)}`
                          : isFailed
                          ? `0 0 0 3px ${alpha('#ef4444', 0.14)}`
                          : isRunning
                          ? `0 6px 18px ${alpha(effectiveAccent, 0.22)}, 0 0 0 4px ${alpha(effectiveAccent, 0.14)}`
                          : 'none',
                        outline: isSelected ? `2px solid #1e88e5` : 'none',
                        outlineOffset: isSelected ? '3px' : '0px',
                        position: 'relative',
                        zIndex: 1,
                        transition: 'outline 0.18s, box-shadow 0.18s',
                        '&::after': isRunning
                          ? {
                              content: '""',
                              position: 'absolute',
                              inset: -6,
                              borderRadius: '50%',
                              border: `2px solid ${alpha(accent, 0.32)}`,
                              borderTopColor: accent,
                              borderRightColor: accent,
                              borderBottomColor: alpha(accent, 0.55),
                              animation: 'timelineSpinnerRotate 1s linear infinite',
                            }
                          : {},
                        '@keyframes timelineSpinnerRotate': {
                          '0%': { transform: 'rotate(0deg)' },
                          '100%': { transform: 'rotate(360deg)' },
                        },
                      }}
                    >
                      {Icon ? (
                        <Icon
                          sx={{
                            fontSize: 20,
                            animation: isRunning ? 'timelineIconGlow 1.1s ease-in-out infinite' : 'none',
                            '@keyframes timelineIconGlow': {
                              '0%': { opacity: 1 },
                              '50%': { opacity: 0.62 },
                              '100%': { opacity: 1 },
                            },
                          }}
                        />
                      ) : (
                        <Typography sx={{ fontWeight: 700, color: nodeColor }}>{idx + 1}</Typography>
                      )}
                    </Avatar>
                  </Box>

                  {/* Step Label */}
                  <Box sx={{ width: '92%', mx: 'auto', mb: 1 }}>
                    <Typography
                      sx={{
                        fontSize: '0.78rem',
                        fontWeight: isSelected ? 700 : 600,
                        color: isSelected ? '#1e88e5' : '#334155',
                        textAlign: 'center',
                        lineHeight: 1.3,
                        minHeight: '2.6em',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        wordBreak: 'break-word',
                        transition: 'color 0.18s, font-weight 0.18s',
                      }}
                    >
                      {step.title}
                    </Typography>
                  </Box>

                  {/* Status Badge below label */}
                  {isCompleted && (
                    <Chip
                      label="Completed"
                      size="small"
                      sx={{
                        height: 22,
                        fontSize: '0.7rem',
                        color: isSelected ? '#1e88e5' : '#15803d',
                        bgcolor: isSelected ? '#eff6ff' : '#dcfce7',
                        borderRadius: '999px',
                        fontWeight: 700,
                        border: '1px solid',
                        borderColor: isSelected ? '#bfdbfe' : '#bbf7d0',
                      }}
                    />
                  )}
                  {isRunning && (
                    <Chip
                      icon={<CircularProgress size={10} thickness={6} sx={{ color: `${accent} !important` }} />}
                      label="In Progress"
                      size="small"
                      sx={{
                        height: 22,
                        fontSize: '0.7rem',
                        color: accent,
                        bgcolor: alpha(accent, 0.12),
                        borderRadius: '999px',
                        fontWeight: 700,
                        border: '1px solid',
                        borderColor: alpha(accent, 0.2),
                        '& .MuiChip-icon': { ml: '6px', mr: '-3px' },
                      }}
                    />
                  )}
                  {isFailed && (
                    <Chip
                      icon={<CancelRoundedIcon sx={{ fontSize: 10 }} />}
                      label="Failed"
                      size="small"
                      sx={{
                        height: 22,
                        fontSize: '0.7rem',
                        color: '#b91c1c',
                        bgcolor: alpha('#ef4444', 0.08),
                        borderRadius: '999px',
                        fontWeight: 700,
                        border: '1px solid',
                        borderColor: alpha('#ef4444', 0.24),
                        '& .MuiChip-icon': { ml: '6px', mr: '-3px', color: '#ef4444 !important' },
                      }}
                    />
                  )}
                  {isIdle && (
                    <Chip
                      label="Pending"
                      size="small"
                      sx={{
                        height: 22,
                        fontSize: '0.7rem',
                        color: '#475569',
                        bgcolor: '#f1f5f9',
                        borderRadius: '999px',
                        fontWeight: 700,
                        border: '1px solid #e2e8f0',
                      }}
                    />
                  )}
                  {isAwaitingReview && (
                    <Chip
                      label="Awaiting Review"
                      size="small"
                      sx={{
                        height: 22,
                        fontSize: '0.7rem',
                        color: '#c2410c',
                        bgcolor: '#fff7ed',
                        borderRadius: '999px',
                        fontWeight: 700,
                        border: '1px solid #fed7aa',
                      }}
                    />
                  )}
                </Box>
              );
            })}

            {/* Selected Arrow pointing up from the details panel - mathematically positioned and horizontal baseline aligned */}
            {selectedStep && (
              <Box
                sx={{
                  position: 'absolute',
                  bottom: 1,
                  left: `${((effectiveStepIdx + 0.5) / steps.length) * 100}%`,
                  transform: 'translateX(-50%)',
                  width: 0,
                  height: 0,
                  borderLeft: '9px solid transparent',
                  borderRight: '9px solid transparent',
                  borderBottom: '9px solid #1e88e5',
                  zIndex: 10,
                  transition: 'left 200ms ease-out, border-bottom-color 200ms ease-out',
                }}
              />
            )}
          </Stack>

          {/* Details Panel below */}
          {selectedStep && (
            <Box
              key={effectiveStepIdx}
              sx={{
                // mt: 1,
                opacity: 0,
                transform: 'translateY(6px)',
                animation: 'stepPanelReveal 240ms ease-out forwards',
                '@keyframes stepPanelReveal': {
                  '0%': { opacity: 0, transform: 'translateY(6px)' },
                  '100%': { opacity: 1, transform: 'translateY(0)' },
                },
              }}
            >
                <Grid container spacing={0} sx={{ border: '1px solid #e2e8f0', borderRadius: 2, overflow: 'hidden', boxShadow: '0 8px 30px rgba(0, 0, 0, 0.06), 0 2px 8px rgba(0, 0, 0, 0.04)' }}>
                  {/* Column 1 - STEP DETAILS (White BG, e2e8f0 borderRight) */}
                  <Grid
                    item
                    xs={12}
                    md={3.2}
                    sx={{
                      position: 'relative',
                      p: 2.5,
                      bgcolor: '#ffffff',
                      '&::after': {
                        content: '""',
                        position: 'absolute',
                        right: 0,
                        top: 24,
                        bottom: 24,
                        width: '1px',
                        backgroundColor: '#e2e8f0',
                        display: { xs: 'none', md: 'block' }
                      }
                    }}
                  >
                    <Typography variant="caption" sx={{ color: '#1e88e5', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', display: 'block', mb: 2 }}>
                      Step Details
                    </Typography>
                    <Stack spacing={2}>
                      <Box sx={{ mb: 0.5 }}>
                        <Typography variant="caption" sx={{ color: '#94a3b8', display: 'block', mb: 0.4, fontSize: '0.72rem', textTransform: 'uppercase', fontWeight: 600 }}>Step</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 700, color: '#0f172a', fontSize: '0.88rem' }}>{selectedStep.title}</Typography>
                      </Box>
                      {selectedStep.isReviewStep ? (
                        <>
                          {renderFieldRow('Status', (
                            <Chip
                              size="small"
                              label={selectedStep.status || '-'}
                              sx={{ height: 22, fontWeight: 700, border: '1px solid #fed7aa', borderRadius: '999px', fontSize: '0.7rem', bgcolor: '#fff7ed', color: '#c2410c' }}
                            />
                          ))}
                          {renderFieldRow('Entered Step At', selectedStep.enteredStepAt || '—')}
                          {renderFieldRow('Assigned To', selectedStep.assignedTo || '—')}
                          {renderFieldRow('SLA Due By', selectedStep.slaDueBy || '—')}
                          {renderFieldRow('Review Priority', (
                            <Chip
                              size="small"
                              label={selectedStep.reviewPriority || 'Medium'}
                              sx={{ height: 22, fontWeight: 700, border: '1px solid #fed7aa', borderRadius: '999px', fontSize: '0.7rem', bgcolor: '#fff7ed', color: '#c2410c' }}
                            />
                          ))}
                        </>
                      ) : (
                        <>
                          {renderFieldRow('Status', (
                            <Chip
                              size="small"
                              label={selectedStep.status}
                              sx={{
                                height: 22,
                                fontWeight: 700,
                                fontSize: '0.7rem',
                                borderRadius: '999px',
                                border: '1px solid',
                                ...(selectedStep.state === 'completed'
                                  ? { bgcolor: '#eff6ff', color: '#1e88e5', borderColor: '#bfdbfe' }
                                  : selectedStep.state === 'running'
                                  ? { bgcolor: alpha(selectedStep.accent, 0.12), color: selectedStep.accent, borderColor: alpha(selectedStep.accent, 0.2) }
                                  : { bgcolor: '#f1f5f9', color: '#475569', borderColor: '#e2e8f0' })
                              }}
                            />
                          ))}
                          {renderFieldRow('Completed At', selectedStep.completedAt || '—')}
                          {renderFieldRow('Duration', selectedStep.duration || '—')}
                          {selectedStep.assignedTo && selectedStep.assignedTo !== '-' && renderFieldRow('Assigned To', selectedStep.assignedTo)}
                          {selectedStep.slaDueBy && selectedStep.slaDueBy !== '-' && renderFieldRow('SLA Due By', selectedStep.slaDueBy)}
                          {selectedStep.reviewPriority && selectedStep.reviewPriority !== '-' && renderFieldRow('Review Priority', (
                            <Chip
                              size="small"
                              label={selectedStep.reviewPriority || 'Medium'}
                              sx={{ height: 22, fontWeight: 700, border: '1px solid #fed7aa', borderRadius: '999px', fontSize: '0.7rem', bgcolor: '#fff7ed', color: '#c2410c' }}
                            />
                          ))}
                        </>
                      )}
                    </Stack>
                  </Grid>

                  {/* Column 2 - DESCRIPTION (White BG, e2e8f0 borderRight) */}
                  <Grid
                    item
                    xs={12}
                    md={selectedStep.isInternetSearchStep ? 8.8 : 4.8}
                    sx={{
                      position: 'relative',
                      p: 2.5,
                      bgcolor: '#ffffff',
                      '&::after': {
                        content: '""',
                        position: 'absolute',
                        right: 0,
                        top: 24,
                        bottom: 24,
                        width: '1px',
                        backgroundColor: '#e2e8f0',
                        display: { xs: 'none', md: selectedStep.isInternetSearchStep ? 'none' : 'block' }
                      }
                    }}
                  >
                    <Typography variant="caption" sx={{ color: '#1e88e5', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', display: 'block', mb: 2 }}>
                      Description
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#334155', lineHeight: 1.8, fontSize: '0.84rem', mb: 2, whiteSpace: 'pre-line', wordBreak: 'break-word' }}>
                      {selectedStep.description}
                    </Typography>
                    
                    {/* Hide Outcome section for account change internet search step */}
                    {!(selectedStep.isInternetSearchStep && selectedStep.isAccountChange) && (
                      <>
                        <Divider sx={{ my: 2, borderColor: '#e2e8f0' }} />
                        <Typography variant="caption" sx={{ color: '#1e88e5', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', display: 'block', mb: 1 }}>
                          Outcome
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#334155', fontWeight: 600, lineHeight: 1.8, fontSize: '0.84rem', wordBreak: 'break-word', mb: 2 }}>
                          {selectedStep.outcome}
                        </Typography>
                      </>
                    )}

                    {selectedStep.isReviewStep && selectedStep.whatHappensNext && selectedStep.whatHappensNext !== '-' && (
                      <>
                        <Divider sx={{ my: 2, borderColor: '#e2e8f0' }} />
                        <Typography variant="caption" sx={{ color: '#1e88e5', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', display: 'block', mb: 1 }}>
                          What Happens Next?
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#334155', lineHeight: 1.8, fontSize: '0.84rem' }}>
                          {selectedStep.whatHappensNext}
                        </Typography>
                      </>
                    )}

                    {selectedStep.isInternetSearchStep && (
                      <Box sx={{ pt: 1, borderTop: '1px solid #e2e8f0' }}>
                        {selectedStep.isAccountChange ? (
                          // Account change: show only Current Address with flat evidence values
                      
                          <Grid container spacing={4}>
                            <Grid item xs={12}>
                              <Typography variant="caption" sx={{ color: '#1e88e5', fontWeight: 700, textTransform: 'uppercase', display: 'block', pb: 1.5, letterSpacing: '0.06em' }}>
                                Outcome
                              </Typography>
                              <Box>
                                <EvidenceGrid meta={selectedStep.currentAddressMeta} />
                              </Box>
                            </Grid>
                          </Grid>
                        ) : (
                          // Address change: show both Current Address and Requested Address
                          <Grid container spacing={4}>
                            <Grid item xs={6}>
                              <Typography variant="caption" sx={{ color: '#1e88e5', fontWeight: 700, textTransform: 'uppercase', display: 'block', mb: 2, borderBottom: '1px solid #e2e8f0', pb: 1.5, letterSpacing: '0.06em' }}>
                                Current Address
                              </Typography>
                              <Box>
                                <EvidenceGrid meta={selectedStep.currentAddressMeta} />
                              </Box>
                            </Grid>
                            <Grid item xs={6}>
                              <Typography variant="caption" sx={{ color: '#1e88e5', fontWeight: 700, textTransform: 'uppercase', display: 'block', mb: 2, borderBottom: '1px solid #e2e8f0', pb: 1.5, letterSpacing: '0.06em' }}>
                                Requested Address
                              </Typography>
                              <Box>
                                <EvidenceGrid meta={selectedStep.requestAddressMeta} />
                              </Box>
                            </Grid>
                          </Grid>
                        )}
                      </Box>
                    )}
                  </Grid>

                  {/* Column 3 - EVIDENCE SUMMARY (White BG) */}
                  {!selectedStep.isInternetSearchStep && (
                    <Grid item xs={12} md={4} sx={{ p: 2.5, bgcolor: '#ffffff', flexGrow: 1 }}>
                      <>
                        <Typography variant="caption" sx={{ color: '#1e88e5', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', display: 'block', mb: 2 }}>
                          {selectedStep.evidenceHeader || 'Evidence'}
                        </Typography>
                        
                        {selectedStep.isEvidenceSummaryMode ? (
                      <Stack spacing={2.2}>
                        <Stack spacing={1.5}>
                          {selectedStep.evidence.map((ev, i) => {
                            const IconColor = ev.isGreen ? '#16a34a' : '#ea580c';
                            const RightIconComp = ev.isGreen ? CheckCircleRoundedIcon : WarningAmberRoundedIcon;
                            
                            // Left avatar icon
                            let LeftIconComp = ArticleRoundedIcon;
                            if (ev.leftIconType === 'settings') LeftIconComp = SettingsRoundedIcon;
                            else if (ev.leftIconType === 'shield') LeftIconComp = ShieldRoundedIcon;
                            else if (ev.leftIconType === 'person') LeftIconComp = PersonRoundedIcon;

                            return (
                              <Box key={i} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1.2, borderBottom: '1px solid #e2e8f0' }}>
                                <Stack direction="row" spacing={1.2} alignItems="center">
                                  <Avatar sx={{ width: 28, height: 28, bgcolor: alpha(IconColor, 0.1), color: IconColor }}>
                                    <LeftIconComp sx={{ fontSize: 16 }} />
                                  </Avatar>
                                  <Typography variant="caption" sx={{ color: '#334155', fontWeight: 600, fontSize: '0.78rem' }}>
                                    {ev.label}
                                  </Typography>
                                </Stack>
                                <RightIconComp sx={{ fontSize: 18, color: IconColor }} />
                              </Box>
                            );
                          })}
                        </Stack>
                        {selectedStep.notes && (
                          <>
                            <Divider sx={{ my: 1.5, borderColor: '#e2e8f0' }} />
                            <Box>
                              <Typography variant="caption" sx={{ color: '#1e88e5', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', display: 'block', mb: 0.75 }}>
                                Notes
                              </Typography>
                              <Typography variant="body2" sx={{ color: '#475569', fontSize: '0.78rem', fontStyle: 'italic', lineHeight: 1.5 }}>
                                {selectedStep.notes}
                              </Typography>
                            </Box>
                          </>
                        )}
                      </Stack>
                    ) : (
                      <Stack spacing={1.2}>
                        {selectedStep.evidence.map((ev, i) => {
                          if (ev.label === 'Detailed Query') {
                            return (
                              <Accordion 
                                key={i}
                                disableGutters 
                                elevation={0} 
                                sx={{ 
                                  borderBottom: i < selectedStep.evidence.length - 1 ? '1px solid #e2e8f0' : 'none',
                                  '&:before': { display: 'none' },
                                  backgroundColor: 'transparent'
                                }}
                              >
                                <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ fontSize: 18, color: '#64748b' }} />} sx={{ px: 0, minHeight: 'auto', '& .MuiAccordionSummary-content': { my: 1.2 } }}>
                                  <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 500, fontSize: '0.78rem' }}>
                                    {ev.label}
                                  </Typography>
                                </AccordionSummary>
                                <AccordionDetails sx={{ px: 0, pt: 0, pb: 1.2 }}>
                                  <Box sx={{ bgcolor: '#f8fafc', p: 1.5, borderRadius: 1, border: '1px solid #e2e8f0', maxHeight: 200, overflowY: 'auto' }}>
                                    <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem', whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
                                      {String(ev.rawValue ?? ev.value).replace(/LEO/g, 'Goodlabs')}
                                    </Typography>
                                  </Box>
                                </AccordionDetails>
                              </Accordion>
                            );
                          }
                          if (selectedStep.isPrescriptionRetrievalStep && String(ev.label || '').toLowerCase() === 'query') {
                            return (
                              <Box
                                key={i}
                                sx={{
                                  pb: 1.2,
                                  borderBottom: i < selectedStep.evidence.length - 1 ? '1px solid #e2e8f0' : 'none',
                                }}
                              >
                                <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 500, fontSize: '0.78rem', display: 'block', mb: 0.8 }}>
                                  Query
                                </Typography>
                                <Box
                                  sx={{
                                    bgcolor: '#f8fafc',
                                    border: '1px solid #e2e8f0',
                                    borderRadius: 1,
                                    p: 1.25,
                                    maxHeight: 260,
                                    overflow: 'auto',
                                  }}
                                >
                                  <Typography
                                    component="pre"
                                    sx={{
                                      m: 0,
                                      color: '#0f172a',
                                      fontFamily: '"SFMono-Regular", Consolas, "Liberation Mono", Menlo, monospace',
                                      fontSize: '0.72rem',
                                      lineHeight: 1.6,
                                      whiteSpace: 'pre-wrap',
                                      wordBreak: 'break-word',
                                    }}
                                  >
                                    {String(ev.rawValue ?? ev.value ?? '-').replace(/LEO/g, 'Goodlabs')}
                                  </Typography>
                                </Box>
                              </Box>
                            );
                          }
                          const isStackedReason = selectedStep.title === 'Human Review Required' || String(ev.label).toLowerCase() === 'reason';
                          return (
                          <Box
                            key={i}
                            onClick={isPreviewableEvidenceLabel(ev.label) ? () => {
                              if (String(ev.label).toLowerCase().includes('website')) {
                                let url = (ev.rawValue && ev.rawValue.website) || ev.rawValue || ev.value;
                                if (url && typeof url === 'string') {
                                  if (!url.startsWith('http')) url = 'https://' + url;
                                  window.open(url, '_blank', 'noopener,noreferrer');
                                }
                              } else {
                                openEvidenceDialog(ev.label, ev.rawValue ?? ev.value);
                              }
                            } : undefined}
                            sx={{
                              display: 'flex',
                              flexDirection: isStackedReason ? 'column' : 'row',
                              alignItems: isStackedReason ? 'flex-start' : 'center',
                              justifyContent: 'space-between',
                              gap: isStackedReason ? 1 : 1.5,
                              pb: 1.2,
                              borderBottom: i < selectedStep.evidence.length - 1 ? '1px solid #e2e8f0' : 'none',
                              cursor: isPreviewableEvidenceLabel(ev.label) ? 'pointer' : 'default',
                            }}
                          >
                            <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 500, flexShrink: 0, fontSize: '0.78rem' }}>
                              {ev.label}
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: isStackedReason ? 'flex-start' : 'flex-end', gap: 0.5, maxWidth: isStackedReason ? '100%' : '65%' }}>
                              {String(ev.label).toLowerCase().includes('website') ? (
                                <Button
                                  size="small"
                                  variant="text"
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    let url = (ev.rawValue && ev.rawValue.website) || ev.rawValue || ev.value;
                                    if (url && typeof url === 'string') {
                                      if (!url.startsWith('http')) url = 'https://' + url;
                                      window.open(url, '_blank', 'noopener,noreferrer');
                                    }
                                  }}
                                  sx={{
                                    minWidth: 0,
                                    px: 0,
                                    py: 0,
                                    height: 'auto',
                                    textTransform: 'none',
                                    fontSize: '0.78rem',
                                    fontWeight: 700,
                                    color: '#1d4ed8',
                                    textDecoration: 'underline',
                                    textUnderlineOffset: '2px',
                                    '&:hover': {
                                      backgroundColor: 'transparent',
                                      color: '#2563eb',
                                    },
                                  }}
                                >
                                  {getEvidenceDisplayText(ev)}
                                </Button>
                              ) : isPreviewableEvidenceLabel(ev.label) ? (
                                <Button
                                  size="small"
                                  variant="outlined"
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    openEvidenceDialog(
                                      getEvidenceDialogTitle(ev.label),
                                      ev.rawValue ?? ev.value
                                    );
                                  }}
                                  sx={{
                                    minWidth: 0,
                                    height: 22,
                                    px: 1,
                                    py: 0,
                                    borderRadius: '999px',
                                    textTransform: 'none',
                                    fontSize: '0.7rem',
                                    fontWeight: 700,
                                  }}
                                  >
                                  {ev.label === 'Query'
                                    ? 'Preview Query'
                                    : 'Preview Website'}
                                </Button>
                              ) : ev.isBadge ? (
                                <Chip
                                  size="small"
                                  label={ev.value}
                                  sx={{
                                    height: 20,
                                    fontSize: '0.7rem',
                                    fontWeight: 700,
                                    bgcolor: alpha(ev.badgeColor || '#64748b', 0.12),
                                    color: ev.badgeColor || '#64748b',
                                    border: '1px solid',
                                    borderColor: alpha(ev.badgeColor || '#64748b', 0.24),
                                    borderRadius: '4px',
                                    px: 0.5
                                  }}
                                />
                              ) : (
                                <>
                                  {ev.isCheck && (
                                    <CheckCircleRoundedIcon sx={{ fontSize: 16, color: '#16a34a', mr: 0.5 }} />
                                  )}
                                  {ev.isCross && (
                                    <CancelRoundedIcon sx={{ fontSize: 16, color: '#ef4444', mr: 0.5 }} />
                                  )}
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      fontWeight: 700,
                                      color: ev.isCross ? '#ef4444' : ev.isCheck ? '#16a34a' : '#0f172a',
                                      textAlign: isStackedReason ? 'left' : 'right',
                                      fontSize: '0.78rem',
                                      wordBreak: 'break-word'
                                    }}
                                  >
                                    {ev.value}
                                  </Typography>
                                </>
                              )}
                            </Box>
                          </Box>
                          );
                        })}
                      </Stack>
                    )}
                      </>
                    </Grid>
                  )}
                </Grid>
            </Box>
          )}
        </Box>
      </Collapse>

      <Dialog
        open={detailDialog.open}
        onClose={closeEvidenceDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 4,
            overflow: 'hidden',
            border: '1px solid',
            borderColor: '#dbe4f0',
            boxShadow: '0 24px 80px rgba(15, 23, 42, 0.22)',
            background: '#ffffff',
          },
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
            px: 3,
            py: 2,
            borderBottom: '1px solid #e2e8f0',
            background: 'linear-gradient(180deg, #ffffff 0%, #f8fbff 100%)',
          }}
        >
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600, color: '#0f172a', lineHeight: 1.2,fontSize: '16px' }}>
              {detailDialog.title}
            </Typography>
            {/* <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 500 }}>
              Review the generated SQL before copying it into your workflow
            </Typography> */}
          </Box>
          <Box>
          <Tooltip title={copiedDialogContent ? 'Copied' : 'Copy'} placement="top">
            <IconButton
              size="small"
              onClick={copyDialogContent}
              aria-label="Copy preview content"
                sx={{
                  width: 36,
                  height: 36,
                  border: '1px solid #e2e8f0',
                  borderRadius: 2,
                  color: '#64748b',
                  bgcolor: '#f8fafc',
                  '&:hover': {
                    bgcolor: '#eef4ff',
                    color: '#1e88e5',
                  },
                }}
              >
              <ContentCopyRoundedIcon sx={{ fontSize: 18 }} />
            </IconButton>
             </Tooltip>
            <IconButton
              size="small"
              onClick={closeEvidenceDialog}
              // aria-label="Copy preview content"
                sx={{
                  width: 36,
                  height: 36,
                  border: '1px solid #e2e8f0',
                  borderRadius: 2,
                  color: '#64748b',
                  bgcolor: '#f8fafc',
                  marginLeft: 1,
                  '&:hover': {
                    bgcolor: '#fff1ee',
                    color: '#e5391e',
                  },
                }}
              >
              <CloseIcon sx={{ fontSize: 18 }} />
            </IconButton>
         </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 1.5, bgcolor: '#f8fafc' ,marginTop:"12px"}}>
          <Box
            sx={{
              p: 2.25,
              borderRadius: 2,
              border: '1px solid #dbe4f0',
              bgcolor: '#ffffff',
              boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.8)',
            }}
          >
            {renderDialogContent(detailDialog.content, detailDialog.title)}
          </Box>
        </DialogContent>
        {/* <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid #e2e8f0', bgcolor: '#fff' }}>
          <Button
            onClick={closeEvidenceDialog}
            variant="outlined"
            sx={{
              textTransform: 'none',
              borderRadius: 999,
              px: 2,
              fontWeight: 600,
            }}
          >
            Close
          </Button>
        </DialogActions> */}
      </Dialog>
    </Box>
  );
};

WorkflowTimelineBottomBar.propTypes = {
  workflowSteps: PropTypes.array,
  agentStatuses: PropTypes.array,
  timelineUsesMockData: PropTypes.bool,
  agentOverallStatus: PropTypes.string,
  timelineLastUpdated: PropTypes.string,
};

export default WorkflowTimelineBottomBar;
